import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, ChevronDown, ChevronUp, RotateCcw, Lock, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { SABOTEUR_PROFILES, SABOTEUR_ORDER } from "@/components/saboteur/saboteurProfileData";

function getRetakeDaysRemaining(completedAt) {
  if (!completedAt) return 0;
  const completed = new Date(completedAt);
  if (isNaN(completed.getTime())) return 0;
  const unlockDate = new Date(completed);
  unlockDate.setDate(unlockDate.getDate() + 90);
  const now = new Date();
  const diff = Math.ceil((unlockDate - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

export default function SaboteurResult({ result, user, saving, completedAt, onRetake }) {
  const [expandedCard, setExpandedCard] = useState(0);
  const primary = SABOTEUR_PROFILES[result.primarySaboteur];
  const dominants = result.dominants; // top 3 keys

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      className="px-4 py-10"
    >
      <div className="max-w-2xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center">
          <p className="text-xs font-medium uppercase tracking-widest mb-3"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}>
            Teste de Sabotadores
          </p>
          <h1 className="text-2xl font-bold mb-2"
            style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Seus sabotadores revelados
          </h1>
          <p className="text-sm"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}>
            {user?.full_name ? `${user.full_name.split(" ")[0]}, estes` : "Estes"} sao os padroes internos que mais impactam sua carreira.
          </p>
        </div>

        {/* Top 3 Dominant Saboteurs */}
        <div className="space-y-4">
          {dominants.map((key, i) => {
            const profile = SABOTEUR_PROFILES[key];
            if (!profile) return null;
            const score = result.scores[key];
            const isExpanded = expandedCard === i;
            const rankLabels = ["Sabotador principal", "2o mais forte", "3o mais forte"];
            return (
              <div
                key={key}
                className="rounded-2xl p-5 bg-white transition-all"
                style={{
                  border: `1.5px solid ${i === 0 ? profile.color : "#E5E9EA"}`,
                  boxShadow: "0 4px 16px -2px rgba(0,0,0,0.06), 0 1px 3px 0 rgba(0,0,0,0.04)",
                }}
              >
                {/* Header row */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: profile.colorLight }}>
                    <ShieldAlert className="w-4 h-4" style={{ color: profile.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wider mb-0.5"
                      style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}>
                      {rankLabels[i]}
                    </p>
                    <h3 className="text-base font-bold truncate"
                      style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
                      {profile.name}
                    </h3>
                  </div>
                  <span className="text-2xl font-bold flex-shrink-0" style={{ color: profile.color }}>
                    {score}%
                  </span>
                </div>

                {/* Score bar */}
                <div className="w-full h-1.5 rounded-full mb-4" style={{ background: "#F2F4F4" }}>
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: profile.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${score}%` }}
                    transition={{ duration: 0.8, delay: 0.2 + i * 0.15 }}
                  />
                </div>

                {/* Expand/collapse */}
                <button
                  onClick={() => setExpandedCard(isExpanded ? -1 : i)}
                  className="flex items-center gap-1.5 text-xs font-medium mb-1 transition-colors"
                  style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
                >
                  {isExpanded
                    ? <><ChevronUp className="w-3 h-3" /> Fechar detalhes</>
                    : <><ChevronDown className="w-3 h-3" /> Ver detalhes</>
                  }
                </button>

                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.25 }}
                    className="mt-3 space-y-4"
                  >
                    <p className="text-sm" style={{ fontFamily: "Work Sans, sans-serif", color: "#2E3233" }}>
                      {profile.description}
                    </p>

                    <div>
                      <p className="text-xs font-semibold mb-2" style={{ fontFamily: "Work Sans, sans-serif", color: profile.color }}>
                        Como te sabota
                      </p>
                      <p className="text-sm" style={{ fontFamily: "Work Sans, sans-serif", color: "#2E3233" }}>
                        {profile.howItSabotages}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-semibold mb-2" style={{ fontFamily: "Work Sans, sans-serif", color: "#00664C" }}>
                        Sinais no dia a dia
                      </p>
                      <ul className="space-y-1">
                        {profile.dailySigns.map((s, j) => (
                          <li key={j} className="text-sm flex items-start gap-2"
                            style={{ fontFamily: "Work Sans, sans-serif", color: "#2E3233" }}>
                            <span style={{ color: profile.color }}>•</span> {s}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="rounded-xl p-4" style={{ background: "#EAFAF6", border: "1px solid #B0FCEA" }}>
                      <p className="text-xs font-semibold mb-1" style={{ fontFamily: "Work Sans, sans-serif", color: "#00664C" }}>
                        Antidoto
                      </p>
                      <p className="text-sm" style={{ fontFamily: "Work Sans, sans-serif", color: "#003326" }}>
                        {profile.antidote}
                      </p>
                    </div>

                    {/* Portus actions */}
                    {profile.portusActions?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold" style={{ fontFamily: "Work Sans, sans-serif", color: "#00664C" }}>
                          Proximos passos na Portus
                        </p>
                        {profile.portusActions.map((action, j) => (
                          <Link key={j} to={createPageUrl(action.page)} className="block">
                            <button className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
                              style={{ fontFamily: "Work Sans, sans-serif", border: "1.5px solid #00DCA4", color: "#00664C", background: "#fff" }}>
                              <ExternalLink className="w-3.5 h-3.5" /> {action.label}
                            </button>
                          </Link>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>

        {/* All Saboteurs Ranking */}
        <div className="rounded-2xl p-5 bg-white"
          style={{ border: "1px solid #E5E9EA", boxShadow: "0 1px 2px 0 rgba(0,0,0,0.04)" }}>
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert className="w-4 h-4" style={{ color: "#00DCA4" }} />
            <p className="text-sm font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
              Ranking completo — 10 sabotadores
            </p>
          </div>

          <div className="space-y-1">
            {result.ranking.map((key, i) => {
              const profile = SABOTEUR_PROFILES[key];
              if (!profile) return null;
              const score = result.scores[key];
              return (
                <div key={key} className="flex items-center gap-3 py-2">
                  <span className="text-xs font-bold w-5 text-right flex-shrink-0"
                    style={{ color: i < 3 ? profile.color : "#99A6AB" }}>
                    #{i + 1}
                  </span>
                  <p className="flex-1 text-sm font-medium truncate"
                    style={{ fontFamily: "Work Sans, sans-serif", color: i < 3 ? "#1A1D1E" : "#646C6F" }}>
                    {profile.name}
                  </p>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-20 h-1.5 rounded-full" style={{ background: "#F2F4F4" }}>
                      <div className="h-full rounded-full" style={{
                        background: i < 3 ? profile.color : "#B0FCEA",
                        width: `${score}%`,
                      }} />
                    </div>
                    <span className="text-xs font-bold w-8 text-right"
                      style={{ color: i < 3 ? profile.color : "#99A6AB" }}>
                      {score}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Juiz universal note */}
        <div className="rounded-2xl p-4 text-center" style={{ background: "#FEE2E2", border: "1px solid #FECACA" }}>
          <p className="text-xs font-semibold mb-1" style={{ fontFamily: "Work Sans, sans-serif", color: "#DC2626" }}>
            O Juiz e universal
          </p>
          <p className="text-xs" style={{ fontFamily: "Work Sans, sans-serif", color: "#7F1D1D" }}>
            Todos nos temos o Juiz ativo. Sua pontuacao foi de {result.juizScore}%. Quanto mais consciente dele, menos poder ele tem sobre voce.
          </p>
        </div>

        {/* Retake + Saving */}
        <div className="flex flex-col items-center gap-3 pt-4">
          {saving && (
            <p className="text-center text-xs"
              style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}>
              Salvando resultado...
            </p>
          )}
          {onRetake && (() => {
            const daysRemaining = getRetakeDaysRemaining(completedAt);
            const locked = daysRemaining > 0;
            return (
              <div className="flex flex-col items-center gap-1.5">
                <button
                  onClick={locked ? undefined : onRetake}
                  disabled={locked}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    fontFamily: "Work Sans, sans-serif",
                    background: locked ? "#F2F4F4" : "#FAFBFB",
                    color: locked ? "#C8CED0" : "#646C6F",
                    border: `1px solid #E5E9EA`,
                    cursor: locked ? "not-allowed" : "pointer",
                  }}
                >
                  {locked
                    ? <Lock className="w-3.5 h-3.5" />
                    : <RotateCcw className="w-3.5 h-3.5" />
                  }
                  Refazer teste
                </button>
                {locked && (
                  <p className="text-[11px]"
                    style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}>
                    disponivel em {daysRemaining} {daysRemaining === 1 ? "dia" : "dias"}
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </motion.div>
  );
}
